import { useState, useEffect, useRef, useMemo } from 'react'
import { chart as rawChart } from '@rawgraphs/rawgraphs-core'
import { mapDataInWorker } from '../worker'
import { WEBWORKER_ACTIVE } from '../constants'
import { normalizeChartMapping } from '../services/normalizeChartMapping'
import { CHART_IDS } from '../charts/chartIds'

function toMappingError(err) {
  const details = String(err?.message || 'Unable to prepare this chart.')
    .replace(/rawgraphs/gi, 'Lugsail')

  return {
    variant: 'danger',
    message: `Data mapping error. ${details}`,
  }
}

export default function useMappedData({
  chart,
  dataset,
  dataTypes,
  mapping,
  datasetRowCount = 0,
  setMappingLoading,
}) {
  const [mappedData, setMappedData] = useState(null)
  const [mappingError, setMappingError] = useState(null)
  const [loadingMessage, setLoadingMessage] = useState('Updating preview…')
  const lastChartIdRef = useRef(chart?.metadata?.id)
  const lastMappingRef = useRef(JSON.stringify(mapping))

  const normalizedMapping = useMemo(
    () => normalizeChartMapping(chart, mapping, dataTypes),
    [chart, mapping, dataTypes]
  )

  useEffect(() => {
    let cancelled = false

    try {
      const currentChartId = chart?.metadata?.id || ''
      const serializedMapping = JSON.stringify(mapping)
      const chartChanged = lastChartIdRef.current !== currentChartId
      const mappingChanged = lastMappingRef.current !== serializedMapping
      const rowCountLabel =
        datasetRowCount >= 5000 ? ` for ${datasetRowCount.toLocaleString()} rows` : ''

      if (chartChanged || !mappedData) {
        setLoadingMessage(`Preparing chart${rowCountLabel}…`)
      } else if (mappingChanged) {
        setLoadingMessage(`Updating preview${rowCountLabel}…`)
      } else {
        setLoadingMessage(`Refreshing preview${rowCountLabel}…`)
      }

      lastChartIdRef.current = currentChartId
      lastMappingRef.current = serializedMapping
      setMappingLoading(true)
      setMappingError(null)

      // Locally authored charts are bundled into the worker at startup. Map
      // this graph on the main thread so development hot-reloads cannot leave
      // the worker validating an older dimension definition.
      const mapInWorker = WEBWORKER_ACTIVE
        && chart?.metadata?.id !== CHART_IDS.FORCE_DIRECTED_GRAPH
      if (mapInWorker) {
        mapDataInWorker(
          chart.metadata.name,
          { data: dataset, mapping: normalizedMapping, dataTypes },
          chart.rawCustomChart
        )
          .then((result) => {
            if (cancelled) return
            setMappingLoading(false)
            setMappedData(result)
            setMappingError(null)
          })
          .catch((err) => {
            if (cancelled) return
            console.error(err)
            setMappingLoading(false)
            setMappedData(null)
            setMappingError(toMappingError(err))
          })
      } else {
        const viz = rawChart(chart, {
          data: dataset,
          mapping: normalizedMapping,
          dataTypes,
        })
        const vizData = viz._getVizData()
        if (!cancelled) {
          setMappingLoading(false)
          setMappedData(vizData)
          setMappingError(null)
        }
      }
    } catch (e) {
      if (cancelled) return
      console.error(e)
      setMappingLoading(false)
      setMappedData(null)
      setMappingError(toMappingError(e))
    }

    return () => {
      cancelled = true
    }
  }, [chart, mapping, normalizedMapping, dataTypes, dataset, setMappingLoading, datasetRowCount])

  return { mappedData, loadingMessage, normalizedMapping, mappingError }
}
